import { Component, inject, OnInit, ViewChild } from '@angular/core';
import { Select, Store } from '@ngxs/store';
import { GeneralLedgerService } from '../../../../services/general-ledger.service';
import {
  GeneralLedgerState,
  GlClearSelected,
  GlLoadConfigs,
  GlLoadQboAccounts,
  GlSelectedConfigChanged,
  GlTabChanged
} from '../../../../state/GeneralLedgerState/GeneralLedger.state';
import { Observable } from 'rxjs';
import {
  ApprovalTypes,
  GeneralLedgerDownloadTypes,
  GeneralLedgerExportFrequencies,
  GeneralLedgerExportTypes,
  GLConfiguration
} from '../../../../models/general-ledger';
import { ClientSelectionState } from '../../../../state/ClientSelectionState/ClientSelection.state';
import { ModalService } from '../../../../services/modal.service';
import { take } from 'rxjs/operators';
import { ExportFilesPrimaryComponent } from '../gl-export-files-tab/export-files-primary/export-files-primary.component';
import { ModalStates } from '../../../../models/modal';
import { ToastService } from '../../../../services/toast.service';

@Component({
  selector: 'lib-gl-settings-tab',
  templateUrl: './gl-settings-tab.component.html',
  styleUrls: ['./gl-settings-tab.component.scss'],
  standalone: false
})
export class GlSettingsTabComponent implements OnInit {
  @ViewChild('exportFilesPrimary')
  exportFilesPrimary: ExportFilesPrimaryComponent;

  @Select(GeneralLedgerState.getConfigs)
  public configs: Observable<GLConfiguration[]>;

  @Select(GeneralLedgerState.getSelectedConfigObject)
  public currentConfig: Observable<GLConfiguration>;

  public editMapping: boolean;
  private readonly toastService = inject(ToastService);

  constructor(
    private store: Store,
    private glService: GeneralLedgerService,
    private modalService: ModalService
  ) {}

  ngOnInit() {
    this.store.dispatch(new GlLoadConfigs());
    this.store.dispatch(new GlClearSelected());
  }

  public addNew() {
    //Set the Create New option as selected, then change tabs.
    this.store.dispatch(new GlSelectedConfigChanged(0)).subscribe(_ => {
      this.store.dispatch(new GlTabChanged(1));
    });
  }

  public duplicate(item: GLConfiguration) {
    this.store.selectOnce(ClientSelectionState.clientCode).subscribe(code => {
      this.glService.duplicateGlConfig(code, item.Id).subscribe(_ => {
        this.store.dispatch(new GlLoadConfigs());
        this.toastService.show(`${item.Name} successfully duplicated`);
      });
    });
  }

  public delete(item: GLConfiguration) {
    this.modalService.confirmModalCallback.pipe(take(1)).subscribe(result => {
      if (result) {
        this.store.selectOnce(ClientSelectionState.clientCode).subscribe(code => {
          this.glService.deleteGlConfig(code, item.Id).subscribe(_ => {
            this.store.dispatch(new GlLoadConfigs());
          });
        });
      }
    });
    this.modalService.showConfirmModal(
      'Are you sure you want to delete this GL export? This action is permanent and cannot be reversed.',
      'Cancel',
      'Delete Configuration',
      'Delete Configuration'
    );
  }

  public showSettings() {
    this.store.dispatch(new GlClearSelected()).subscribe(_ => {
      this.editMapping = false;
    });
  }

  public showEditMapping(connectedToQbo: boolean = false) {
    this.store.selectOnce(GeneralLedgerState.getSelectedConfigObject).subscribe(config => {
      if (config.ExportFileType === GeneralLedgerExportTypes.QBO) {
        this.store.dispatch(new GlLoadQboAccounts(config.Id)).subscribe(
          _ => {
            this.editMapping = true;
          },
          err => {
            let message = `There was a problem connecting to Quickbooks Online. Please reconnect "${config.Name}" to Quickbooks Online using the controls on this page.`;
            if (connectedToQbo) {
              message = `Please save the form before trying to edit mappings. Your Quickbooks Online data has not yet been saved.`;
            }
            this.modalService.showAlertModal(
              message,
              ModalStates.Error,
              null,
              'Error connecting to Quickbooks Online.'
            );
          }
        );
      } else {
        this.editMapping = true;
      }
    });
  }

  public updateConfig(config: GLConfiguration) {
    this.store.dispatch(new GlSelectedConfigChanged(config.Id)).subscribe(_ => {
      this.editMapping = false;
    });
  }

  protected readonly GeneralLedgerExportTypes = GeneralLedgerExportTypes;
  protected readonly ApprovalTypes = ApprovalTypes;
  protected readonly GeneralLedgerExportFrequencies = GeneralLedgerExportFrequencies;
  protected readonly GeneralLedgerDownloadTypes = GeneralLedgerDownloadTypes;
}
